import { WritableSignal } from '@angular/core';
import { finalize, Observable, tap } from 'rxjs';

/**
 * A custom RxJS operator that automatically manages a loading signal
 * for the duration of an observable's execution.
 * Sets the signal to `true` on subscription and `false` once the
 * observable completes, errors, or is unsubscribed.
 *
 * @param loadingSignal - A writable signal to toggle during the observable's lifecycle
 * @returns A pipeable RxJS operator that wraps the source observable with loading state management
 *
 * @example
 * forkJoin({ users: this.usersService.getAll() }).pipe(
 *     withLoading(this.isLoading),
 *     takeUntilDestroyed(this.destroyRef),
 * ).subscribe();
 */
export function withLoading<T>(loadingSignal: WritableSignal<boolean>) {
    return (source: Observable<T>): Observable<T> => {
        return source.pipe(
            tap({ subscribe: () => loadingSignal.set(true) }),
            finalize(() => loadingSignal.set(false)),
        );
    };
}
